import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Table from "../../../core/common/dataTable/index";
import { all_routes } from "../../router/all_routes";
import {
  STATUS_FILTER_OPTIONS,
  fetchSelfTransferRequests,
  formatDateDisplay,
  formatHodUnit,
  getLoggedInEmpId,
  groundLabel,
  reasonLabel,
  syncSelfTransferRequests,
} from "./selfTransfer.service";

interface SelfTransferListProps {
  embedded?: boolean;
}

const SelfTransferList: React.FC<SelfTransferListProps> = ({ embedded = false }) => {
  const routes = all_routes;
  const navigate = useNavigate();

  const [requestsList, setRequestsList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [viewMode, setViewMode] = useState<"grid" | "table">("table");
  const [tablePagination, setTablePagination] = useState({
    current: 1,
    pageSize: 10,
  });

  // Filters (Aligned with Mobile: Status, Start Date, End Date)
  const [filters, setFilters] = useState({
    Status: "ALL",
    startDate: "",
    endDate: "",
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetchSelfTransferRequests();
      if (res.success && res.data) {
        setRequestsList(Array.isArray(res.data) ? res.data : []);
      } else {
        setRequestsList([]);
      }
    } catch (err: any) {
      toast.error(err?.message || "Failed to load self transfer requests");
      setRequestsList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSync = async () => {
    const empId = getLoggedInEmpId();
    try {
      setSyncing(true);
      await syncSelfTransferRequests(empId);
      toast.success("Requests synced successfully from SAP");
      await loadData();
    } catch (err: any) {
      toast.error(err?.message || "Failed to sync self transfer requests");
      await loadData();
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getStatusColor = (rawStatus?: string) => {
    const s = String(rawStatus || "").trim().toUpperCase();
    if (["APPROVE", "APPROVED", "A", "S"].includes(s)) return "#28a745"; // Green
    if (["REJECT", "REJECTED", "R", "K", "CANCELLED"].includes(s)) return "#dc3545"; // Red
    if (["INPROCESS", "IN_PROCESS", "PENDING", "T", "P", "NEW", ""].includes(s)) return "#f39c12"; // Amber
    return "#6c757d";
  };

  const getStatusIcon = (rawStatus?: string) => {
    const s = String(rawStatus || "").trim().toUpperCase();
    if (["APPROVE", "APPROVED", "A"].includes(s)) return "ti ti-circle-check";
    if (["REJECT", "REJECTED", "R"].includes(s)) return "ti ti-circle-x";
    return "ti ti-clock-hour-4";
  };

  const getStatusLabel = (rawStatus?: string) => {
    const s = String(rawStatus || "").trim().toUpperCase();
    if (["APPROVE", "APPROVED", "A"].includes(s)) return "Approved";
    if (["REJECT", "REJECTED", "R"].includes(s)) return "Rejected";
    return "In Process";
  };

  const copyRefNumber = (refNo: string) => {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(refNo);
      toast.info(`Copied ${refNo} to clipboard`);
    }
  };

  // Filter application (Mobile-aligned: Status, Start Date, End Date)
  const filteredData = useMemo(() => {
    return requestsList.filter((item) => {
      const st = String(item.Status || item.status || "").toUpperCase();
      if (filters.Status !== "ALL") {
        if (filters.Status === "INPROCESS") {
          if (st !== "INPROCESS" && st !== "PENDING" && st !== "T" && st !== "") return false;
        } else if (filters.Status === "APPROVE") {
          if (st !== "APPROVE" && st !== "APPROVED" && st !== "A") return false;
        } else if (filters.Status === "REJECT") {
          if (st !== "REJECT" && st !== "REJECTED" && st !== "R") return false;
        }
      }

      if (filters.startDate) {
        const itemDate = item.created_at || item.createOn;
        if (itemDate) {
          const d = new Date(itemDate);
          const start = new Date(filters.startDate);
          if (d < start) return false;
        }
      }
      if (filters.endDate) {
        const itemDate = item.created_at || item.createOn;
        if (itemDate) {
          const d = new Date(itemDate);
          const end = new Date(filters.endDate);
          end.setHours(23, 59, 59, 999);
          if (d > end) return false;
        }
      }

      return true;
    });
  }, [requestsList, filters]);

  const handleClearFilters = () => {
    setFilters({
      Status: "ALL",
      startDate: "",
      endDate: "",
    });
  };

  const tableColumns = useMemo(
    () => [
      {
        title: "Reference Number",
        dataIndex: "RequestID",
        key: "RequestID",
        sorter: (a: any, b: any) =>
          String(a.RequestID || a.referenceNumber || "").localeCompare(
            String(b.RequestID || b.referenceNumber || "")
          ),
        render: (_: any, record: any) => {
          const reqId = record.RequestID || record.referenceNumber || record.id || "-";
          return (
            <div className="d-flex align-items-center gap-1.5">
              <span className="fw-bold font-monospace text-dark fs-13">{reqId}</span>
              <button
                type="button"
                className="btn btn-sm btn-link p-0 text-muted"
                title="Copy Reference"
                onClick={() => copyRefNumber(String(reqId))}
              >
                <i className="ti ti-copy fs-14" />
              </button>
            </div>
          );
        },
      },
      {
        title: "Transfer Ground",
        dataIndex: "TransferGround",
        key: "TransferGround",
        sorter: (a: any, b: any) =>
          String(groundLabel(a.TransferGround)).localeCompare(
            String(groundLabel(b.TransferGround))
          ),
        render: (_: any, record: any) => (
          <span className="badge bg-light text-dark border px-2 py-0.5 fs-11 fw-medium">
            {groundLabel(record.TransferGround)}
          </span>
        ),
      },
      {
        title: "Reason",
        dataIndex: "ReasonForTransfer",
        key: "ReasonForTransfer",
        sorter: (a: any, b: any) =>
          String(reasonLabel(a.ReasonForTransfer)).localeCompare(
            String(reasonLabel(b.ReasonForTransfer))
          ),
        render: (_: any, record: any) => (
          <span className="small text-dark fw-medium">
            {reasonLabel(record.ReasonForTransfer)}
          </span>
        ),
      },
      {
        title: "Target Unit / HOD",
        dataIndex: "NameOfHODUnit",
        key: "NameOfHODUnit",
        sorter: (a: any, b: any) =>
          String(formatHodUnit(a.NameOfHODUnit)).localeCompare(
            String(formatHodUnit(b.NameOfHODUnit))
          ),
        render: (_: any, record: any) => (
          <span className="small text-dark">{formatHodUnit(record.NameOfHODUnit)}</span>
        ),
      },
      {
        title: "Creation Date",
        dataIndex: "created_at",
        key: "created_at",
        sorter: (a: any, b: any) => {
          const aDate = new Date(a.created_at || a.createOn || 0).getTime();
          const bDate = new Date(b.created_at || b.createOn || 0).getTime();
          return aDate - bDate;
        },
        render: (_: any, record: any) => (
          <span className="small text-muted">
            {formatDateDisplay(record.created_at || record.createOn)}
          </span>
        ),
      },
      {
        title: "Approver",
        dataIndex: "ApproverID",
        key: "ApproverID",
        render: (_: any, record: any) => (
          <span className="small text-muted font-monospace">
            {record.ApproverID || "00013161"}
          </span>
        ),
      },
      {
        title: "Status",
        dataIndex: "Status",
        key: "Status",
        sorter: (a: any, b: any) =>
          String(a.Status || a.status || "").localeCompare(
            String(b.Status || b.status || "")
          ),
        render: (_: any, record: any) => {
          const status = record.Status || record.status || "INPROCESS";
          const statusColor = getStatusColor(status);
          const statusIcon = getStatusIcon(status);
          const statusLabel = getStatusLabel(status);
          return (
            <span
              className="badge px-2 py-0.5 fs-11 fw-semibold d-inline-flex align-items-center"
              style={{
                color: statusColor,
                backgroundColor: `${statusColor}15`,
                border: `1px solid ${statusColor}44`,
              }}
            >
              <i className={`${statusIcon} me-1 fs-11`} />
              {statusLabel}
            </span>
          );
        },
      },
      {
        title: "Actions",
        key: "actions",
        align: "center" as const,
        width: 110,
        render: (_: any, record: any) => {
          const reqId = record.RequestID || record.referenceNumber || record.id || "-";
          return (
            <div
              className="action-icon d-inline-flex align-items-center justify-content-center"
              style={{ gap: "10px" }}
            >
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center justify-content-center p-0 rounded-circle"
                style={{ width: "32px", height: "32px" }}
                title="Approvers Timeline"
                aria-label="Approvers Timeline"
                onClick={() =>
                  navigate(
                    routes.selfTransfer.approvers.replace(
                      ":id",
                      encodeURIComponent(String(reqId))
                    ),
                    { state: { item: record } }
                  )
                }
              >
                <i className="ti ti-users fs-14" />
              </button>
              <button
                type="button"
                className="btn btn-danger btn-sm text-white d-inline-flex align-items-center justify-content-center p-0 rounded-circle"
                style={{
                  width: "32px",
                  height: "32px",
                  backgroundColor: "#ab1f24",
                  borderColor: "#ab1f24",
                }}
                title="View Details"
                aria-label="View Details"
                onClick={() =>
                  navigate(
                    routes.selfTransfer.details.replace(
                      ":id",
                      encodeURIComponent(String(reqId))
                    ),
                    { state: { item: record } }
                  )
                }
              >
                <i className="ti ti-eye fs-14" />
              </button>
            </div>
          );
        },
      },
    ],
    [routes, navigate]
  );

  const content = (
    <div>
      {/* Criteria Maintenance Filter Card (Standard Enterprise Web Style) */}
      <div className="card mb-4 shadow-sm border">
        <div
          className="card-header bg-light d-flex align-items-center justify-content-between p-3.5 cursor-pointer"
          onClick={() => setIsFilterOpen(!isFilterOpen)}
          style={{ cursor: "pointer" }}
        >
          <h6 className="card-title mb-0 fw-semibold d-flex align-items-center">
            <i className="ti ti-search me-2 text-danger" style={{ fontSize: "18px" }} />
            Quick Criteria Maintenance
          </h6>
          <div className="d-flex align-items-center gap-2">
            {(filters.Status !== "ALL" ||
              filters.startDate ||
              filters.endDate) && (
              <span className="badge bg-danger text-white rounded-pill px-2.5 py-1 fs-11 fw-semibold">
                Active Filter
              </span>
            )}
            <button type="button" className="btn btn-sm btn-outline-secondary p-1" style={{ lineHeight: 1 }}>
              <i className={`ti ti-chevron-${isFilterOpen ? "up" : "down"} fs-14`} />
            </button>
          </div>
        </div>

        {isFilterOpen && (
          <div className="card-body p-4 border-top">
            <div className="row g-3.5 align-items-end">
              <div className="col-md-3 col-sm-6">
                <label className="form-label fs-13 text-muted fw-semibold">Start Date</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={filters.startDate}
                  onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                />
              </div>

              <div className="col-md-3 col-sm-6">
                <label className="form-label fs-13 text-muted fw-semibold">End Date</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={filters.endDate}
                  onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                />
              </div>

              <div className="col-md-3 col-sm-6">
                <label className="form-label fs-13 text-muted fw-semibold">Request Status</label>
                <select
                  className="form-select form-select-sm"
                  value={filters.Status}
                  onChange={(e) => setFilters({ ...filters, Status: e.target.value })}
                >
                  <option value="ALL">All Status</option>
                  <option value="INPROCESS">In Process</option>
                  <option value="APPROVE">Approved</option>
                  <option value="REJECT">Rejected</option>
                </select>
              </div>

              <div className="col-md-3 col-sm-6 d-flex align-items-end justify-content-end gap-2.5">
                <button
                  type="button"
                  className="btn btn-sm btn-primary px-3.5 py-1.5 fw-semibold"
                  style={{ backgroundColor: "#ab1f24", borderColor: "#ab1f24" }}
                  onClick={() => setIsFilterOpen(false)}
                >
                  <i className="ti ti-filter me-1" /> Apply Filters
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary px-3.5 py-1.5 fw-semibold"
                  onClick={handleClearFilters}
                >
                  <i className="ti ti-refresh me-1" /> Reset
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* View Mode Bar */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <span className="text-muted fs-13">
          Showing <strong>{filteredData.length}</strong> {filteredData.length === 1 ? "application" : "applications"}
        </span>
        <div className="btn-group btn-group-sm shadow-xs">
          <button
            type="button"
            className={`btn ${viewMode === "grid" ? "btn-danger text-white fw-semibold" : "btn-outline-secondary"}`}
            style={viewMode === "grid" ? { backgroundColor: "#ab1f24", borderColor: "#ab1f24" } : {}}
            onClick={() => setViewMode("grid")}
          >
            <i className="ti ti-layout-grid me-1" /> Cards
          </button>
          <button
            type="button"
            className={`btn ${viewMode === "table" ? "btn-danger text-white fw-semibold" : "btn-outline-secondary"}`}
            style={viewMode === "table" ? { backgroundColor: "#ab1f24", borderColor: "#ab1f24" } : {}}
            onClick={() => setViewMode("table")}
          >
            <i className="ti ti-list me-1" /> Table
          </button>
        </div>
      </div>

      {/* Content Area */}
      {loading && !syncing ? (
        <div className="d-flex justify-content-center align-items-center py-5">
          <div className="spinner-border text-danger" style={{ color: "#ab1f24" }} role="status" />
          <span className="ms-2 text-muted fw-medium">Loading requests...</span>
        </div>
      ) : filteredData.length === 0 ? (
        <div className="card shadow-sm border text-center py-5">
          <div className="card-body text-muted">
            <i className="ti ti-arrows-exchange fs-1 d-block mb-2 text-secondary opacity-50" />
            <h6 className="fw-bold">No self transfer requests found</h6>
            <p className="small text-muted mb-3">
              {requestsList.length > 0
                ? "No requests match the selected criteria."
                : "You have not submitted any self transfer requests yet."}
            </p>
            <Link
              to={routes.selfTransfer.create}
              className="btn btn-sm btn-danger text-white fw-bold shadow-sm d-inline-flex align-items-center px-3"
              style={{ backgroundColor: "#ab1f24", borderColor: "#ab1f24" }}
            >
              <i className="ti ti-plus me-1" /> Create New Request
            </Link>
          </div>
        </div>
      ) : viewMode === "grid" ? (
        /* Standardized Enterprise Cards (Clean 2-Column Grid, No Nested Roundy Divs) */
        <div className="row g-4 mb-4">
          {filteredData.map((request: any, idx: number) => {
            const reqId = request.RequestID || request.referenceNumber || request.id || `ID: ${idx + 1}`;
            const ground = groundLabel(request.TransferGround);
            const reason = reasonLabel(request.ReasonForTransfer);
            const createdDate = formatDateDisplay(request.created_at || request.createOn);
            const status = request.Status || request.status || "INPROCESS";
            const statusColor = getStatusColor(status);
            const statusIcon = getStatusIcon(status);
            const statusLabel = getStatusLabel(status);
            const hodDisplay = formatHodUnit(request.NameOfHODUnit);

            return (
              <div className="col-12 col-lg-6" key={String(request.id || request.RequestID || idx)}>
                <div
                  className="card h-100 border shadow-sm"
                  style={{
                    borderTop: `3px solid ${statusColor}`,
                  }}
                >
                  {/* Card Header: Reference Number & Status Badge */}
                  <div className="card-header bg-white border-bottom py-3 px-3.5 d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-1.5 overflow-hidden me-2">
                      <span className="text-muted fs-11 text-uppercase fw-bold flex-shrink-0">Ref:</span>
                      <span
                        className="fw-bold text-dark font-monospace text-truncate fs-13"
                        title={String(reqId)}
                      >
                        {reqId}
                      </span>
                      <button
                        type="button"
                        className="btn btn-sm btn-link p-0 text-muted flex-shrink-0"
                        title="Copy Reference"
                        onClick={() => copyRefNumber(String(reqId))}
                      >
                        <i className="ti ti-copy fs-14" />
                      </button>
                    </div>

                    <span
                      className="badge px-2.5 py-1 fw-semibold fs-11 flex-shrink-0 d-inline-flex align-items-center"
                      style={{
                        color: statusColor,
                        backgroundColor: `${statusColor}15`,
                        border: `1px solid ${statusColor}44`,
                      }}
                    >
                      <i className={`${statusIcon} me-1 fs-12`} />
                      {statusLabel}
                    </span>
                  </div>

                  {/* Card Body: Standard 2x2 Key-Value Fields */}
                  <div className="card-body p-3.5">
                    <div className="row g-3">
                      <div className="col-6">
                        <span className="text-muted fs-11 text-uppercase fw-semibold d-block mb-1">
                          Transfer Ground
                        </span>
                        <div className="d-flex align-items-center gap-1 text-dark fw-bold fs-13 text-truncate">
                          <i className="ti ti-arrows-exchange text-danger flex-shrink-0" />
                          <span className="text-truncate">{ground}</span>
                        </div>
                      </div>

                      <div className="col-6">
                        <span className="text-muted fs-11 text-uppercase fw-semibold d-block mb-1">
                          Creation Date
                        </span>
                        <div className="d-flex align-items-center gap-1 text-dark fw-bold fs-13">
                          <i className="ti ti-calendar text-muted flex-shrink-0" />
                          <span>{createdDate}</span>
                        </div>
                      </div>

                      <div className="col-6 pt-1">
                        <span className="text-muted fs-11 text-uppercase fw-semibold d-block mb-1">
                          Reason for Transfer
                        </span>
                        <div className="d-flex align-items-center gap-1 text-dark fw-bold fs-13 text-truncate">
                          <i className="ti ti-clock text-primary flex-shrink-0" />
                          <span className="text-truncate">{reason}</span>
                        </div>
                      </div>

                      <div className="col-6 pt-1">
                        <span className="text-muted fs-11 text-uppercase fw-semibold d-block mb-1">
                          Target Unit / HOD
                        </span>
                        <div className="d-flex align-items-center gap-1 text-dark fw-bold fs-13 text-truncate">
                          <i className="ti ti-building text-success flex-shrink-0" />
                          <span className="text-truncate" title={hodDisplay}>
                            {hodDisplay}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Clean Remarks Strip (No nested roundy box) */}
                    {request.Remarks && (
                      <div className="border-start border-3 border-danger-subtle ps-3 py-2 mt-3.5 text-muted fs-12 bg-light-subtle rounded-end">
                        <span className="fw-semibold text-dark">Remarks:</span> {request.Remarks}
                      </div>
                    )}
                  </div>

                  {/* Card Footer: Metadata Left, Actions Right */}
                  <div className="card-footer bg-light py-2.5 px-3.5 border-top d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <div className="small text-muted fs-12">
                      <span className="text-muted">Emp ID:</span>{" "}
                      <strong className="text-dark">{request.EmpID || getLoggedInEmpId() || "00015368"}</strong>
                      <span className="mx-1 text-muted">•</span>
                      <span className="text-muted">Approver:</span>{" "}
                      <strong className="text-dark">{request.ApproverID || "00013161"}</strong>
                    </div>

                    <div className="d-flex align-items-center gap-1.5 ms-auto">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary px-2.5 py-1 fs-12 d-inline-flex align-items-center gap-1"
                        onClick={() =>
                          navigate(routes.selfTransfer.approvers.replace(":id", encodeURIComponent(String(reqId))), {
                            state: { item: request },
                          })
                        }
                        title="View Approvers Workflow"
                      >
                        <i className="ti ti-users fs-13" /> Approvers
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-danger px-3 py-1 fs-12 fw-semibold d-inline-flex align-items-center gap-1"
                        style={{
                          backgroundColor: "#ab1f24",
                          borderColor: "#ab1f24",
                        }}
                        onClick={() =>
                          navigate(routes.selfTransfer.details.replace(":id", encodeURIComponent(String(reqId))), {
                            state: { item: request },
                          })
                        }
                      >
                        <i className="ti ti-eye fs-13" /> View Details
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* Global Web Data Table (used across all standard DMRC web modules) */
        <div className="card border shadow-sm mb-4">
          <div className="card-body p-0 py-3">
            <Table
              columns={tableColumns}
              dataSource={filteredData}
              Selection={false}
              loading={loading}
              searchPlaceholder="Search reference, employee, ground..."
              paginationConfig={{
                current: tablePagination.current,
                pageSize: tablePagination.pageSize,
                total: filteredData.length,
              }}
              handlePagination={(pag: any) => {
                setTablePagination({
                  current: pag.current || 1,
                  pageSize: tablePagination.pageSize || 10,
                });
              }}
            />
          </div>
        </div>
      )}
    </div>
  );

  if (embedded) return content;

  return (
    <div className="page-wrapper" style={{ backgroundColor: "#f8f9fa" }}>
      <div className="content">
        {/* Page Header (Exact DMRC / Reimbursement Style) */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-4">
          <div className="my-auto mb-2">
            <div className="d-flex align-items-center">
              <Link
                to={routes.employeeDashboard}
                className="text-secondary me-3 text-decoration-none bg-white border d-flex align-items-center justify-content-center shadow-sm"
                style={{ width: "40px", height: "40px", borderRadius: "50%" }}
                title="Back to Home"
              >
                <i className="ti ti-arrow-left" style={{ fontSize: "20px" }} />
              </Link>
              <div>
                <h3 className="page-title mb-1 fw-bold text-dark">Self Transfer Applications</h3>
                <nav>
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <Link to={routes.employeeDashboard}>Home</Link>
                    </li>
                    <li className="breadcrumb-item active text-danger">Self Transfer</li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <button
              type="button"
              className="btn btn-white shadow-sm border px-3 py-2 rounded-3 fw-medium d-flex align-items-center bg-white"
              onClick={handleSync}
              disabled={syncing || loading}
            >
              <i
                className={`ti ti-refresh me-2 text-danger ${syncing ? "fa-spin" : ""}`}
                style={{ fontSize: "18px" }}
              />
              {syncing ? "Syncing..." : "Sync SAP"}
            </button>
            <Link to={routes.selfTransfer.create}>
              <button
                type="button"
                className="btn text-white fw-bold shadow-sm d-flex align-items-center px-4 rounded-3"
                style={{ backgroundColor: "#ab1f24", borderColor: "#ab1f24", height: "40px", fontSize: "13px" }}
              >
                <i className="ti ti-plus me-1" style={{ fontSize: "16px" }} />
                Create New Request
              </button>
            </Link>
          </div>
        </div>

        {content}
      </div>
    </div>
  );
};

export default SelfTransferList;
